'use client';

import { Avatar, Box, Divider, Stack, Typography } from '@mui/material';
import { radius } from '@odys/design-system/tokens/radius';
import Image from 'next/image';

import type { DmcCardDto, DmcVisitorDto } from '@/app/api/lib/dmcs/dmc.types';
import { SurfaceCard } from '@odys/design-system/components/SurfaceCard';

import { Link } from '@/configurations/i18n/navigation';

type DmcCardProps = {
    dmc: DmcCardDto | DmcVisitorDto;
};

export function DmcCard({ dmc }: DmcCardProps) {
    // « Language » (= targetMarket) et spécialités sur deux listes empilées,
    // l'une au-dessus de l'autre (#2146). dmcTags reste groupé avec les
    // spécialités (mêmes descripteurs).
    const markets = dmc.targetMarket;
    const specialties = [
        ...dmc.specialties.map((s) => s.label),
        ...dmc.dmcTags.map((t) => t.label),
    ];
    return (
        <SurfaceCard
            href={`/dmcs/${dmc.slug}`}
            LinkComponent={Link}
            data-testid={`dmc-card-${dmc.slug}`}
            sx={{ p: 1.25 }}
        >
            <Stack
                direction="row"
                spacing={3}
                sx={{
                    alignItems: 'center',
                }}
            >
                <DmcLogo dmc={dmc} />
                <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0, py: 0.5 }}>
                    <Typography
                        variant="h4"
                        component="h3"
                        sx={{
                            color: 'text.primary',
                        }}
                    >
                        {dmc.name}
                    </Typography>
                    <ChipRow labels={markets} testId={`dmc-card-markets-${dmc.slug}`} />
                    <ChipRow labels={specialties} testId={`dmc-card-specialties-${dmc.slug}`} />
                </Stack>
            </Stack>
        </SurfaceCard>
    );
}

function ChipRow({ labels, testId }: { labels: string[]; testId: string }) {
    if (labels.length === 0) return null;
    return (
        <Stack
            direction="row"
            spacing={1}
            divider={<Divider orientation="vertical" flexItem />}
            useFlexGap
            data-testid={testId}
            sx={{
                alignItems: 'center',
                flexWrap: 'wrap',
                color: 'text.secondary',
                lineHeight: 1.43,
            }}
        >
            {labels.map((label, idx) => (
                <Typography key={`${label}-${idx}`} component="span" sx={{ color: 'inherit' }}>
                    {label}
                </Typography>
            ))}
        </Stack>
    );
}

function DmcLogo({ dmc }: { dmc: DmcCardDto | DmcVisitorDto }) {
    if (dmc.logoUrl) {
        return (
            <Box
                sx={{
                    width: { xs: 80, sm: 128 },
                    height: { xs: 80, sm: 128 },
                    borderRadius: radius.sm,
                    overflow: 'hidden',
                    flexShrink: 0,
                    bgcolor: 'common.white',
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 1,
                }}
            >
                <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                    <Image
                        src={dmc.logoUrl}
                        alt=""
                        fill
                        sizes="(max-width:600px) 80px, 128px"
                        style={{ objectFit: 'contain' }}
                    />
                </Box>
            </Box>
        );
    }

    return (
        <Avatar
            data-testid="dmc-logo-fallback"
            variant="rounded"
            sx={{
                width: { xs: 80, sm: 128 },
                height: { xs: 80, sm: 128 },
                bgcolor: 'grey.200',
                color: 'text.primary',
                fontSize: 28,
                fontWeight: 600,
                flexShrink: 0,
            }}
        >
            {dmc.name.slice(0, 2).toUpperCase()}
        </Avatar>
    );
}
